// src/utils/exportHtmlFile.ts
import { saveAs } from "file-saver";
import { asBlob } from "html-docx-js-typescript";

type ExportOptions = {
  fileName?: string;
  title?: string;
};

const getExportFooterText = () => {
  const now = new Date();

  const dateText = now.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const timeText = now.toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return `Export จากระบบ VRISS เมื่อวันที่ ${dateText} เวลา ${timeText} น.`;
};

const wordMimeType =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

  const wrapHtmlForExport = (html: string, title = "") => {
    return `
      <div class="export-document">
        <style>
          .export-document {
            font-family: "TH Sarabun New", "Sarabun", Arial, sans-serif;
            font-size: 16px;
            line-height: 1.6;
            color: #111;
            background: #fff;
            padding: 24px;
            box-sizing: border-box;
            position: relative;
            min-height: 1120px;
          }
  
          .export-document h1,
          .export-document h2,
          .export-document h3 {
            margin: 0 0 12px;
            line-height: 1.3;
          }
  
          .export-document p {
            margin: 0 0 10px;
          }
  
          .export-document table {
            width: 100%;
            border-collapse: collapse;
            margin: 12px 0;
          }
  
          .export-document table,
          .export-document th,
          .export-document td {
            border: 1px solid #333;
          }
  
          .export-document th,
          .export-document td {
            padding: 8px;
            vertical-align: top;
          }
  
          .export-document img {
            max-width: 100%;
            height: auto;
          }
  
          .export-document ul,
          .export-document ol {
            margin-top: 0;
            margin-bottom: 12px;
          }
  
          .export-document blockquote {
            border-left: 4px solid #ddd;
            padding-left: 12px;
            color: #555;
            margin: 12px 0;
          }
  
          .export-document .document-title {
            text-align: center;
            font-size: 22px;
            font-weight: 700;
            margin-bottom: 20px;
          }
        </style>
  
        ${title ? `<div class="document-title">${title}</div>` : ""}
        ${html}
      </div>
    `;
  };

  const createPdfFooterImage = async (footerText: string) => {
    const html2canvas = (await import("html2canvas")).default;
  
    const footer = document.createElement("div");
  
    footer.style.position = "fixed";
    footer.style.left = "-10000px";
    footer.style.top = "0";
    footer.style.width = "794px";
    footer.style.height = "48px";
    footer.style.background = "#ffffff";
    footer.style.boxSizing = "border-box";
    footer.style.padding = "8px 24px 0 24px";
    footer.style.fontFamily = `"TH Sarabun New", "Sarabun", Arial, sans-serif`;
    footer.style.fontSize = "12px";
    footer.style.color = "#8c8c8c";
    footer.style.textAlign = "right";
  
    footer.innerHTML = `
      <div style="border-top: 1px solid #d9d9d9; padding-top: 8px;">
        ${footerText}
      </div>
    `;
  
    document.body.appendChild(footer);
  
    try {
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
  
      const canvas = await html2canvas(footer, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });
  
      return {
        dataUrl: canvas.toDataURL("image/png"),
        width: canvas.width,
        height: canvas.height,
      };
    } finally {
      if (document.body.contains(footer)) {
        document.body.removeChild(footer);
      }
    }
  };
  
  const addFooterToEveryPdfPage = (
    pdf: any,
    footerImage: {
      dataUrl: string;
      width: number;
      height: number;
    }
  ) => {
    const pageCount = pdf.internal.getNumberOfPages();
  
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
  
    const marginX = 10;
    const bottomOffset = 6;
  
    const imageWidth = pageWidth - marginX * 2;
    const imageHeight = (footerImage.height * imageWidth) / footerImage.width;
  
    const x = marginX;
    const y = pageHeight - imageHeight - bottomOffset;
  
    for (let page = 1; page <= pageCount; page += 1) {
      pdf.setPage(page);
      pdf.addImage(
        footerImage.dataUrl,
        "PNG",
        x,
        y,
        imageWidth,
        imageHeight
      );
    }
  };


  const wrapHtmlForDocx = (html: string, title = "") => {
    const footerText = getExportFooterText();
  
    return `
      <!DOCTYPE html>
      <html
        xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:w="urn:schemas-microsoft-com:office:word"
        xmlns="http://www.w3.org/TR/REC-html40"
      >
        <head>
          <meta charset="UTF-8" />
  
          <!-- บังคับให้ Word เปิดแบบ Print Layout เพื่อเห็น Header/Footer -->
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml>
          <![endif]-->
  
          <style>
            @page WordSection1 {
              size: 21cm 29.7cm;
              margin: 1.5cm 1.5cm 2cm 1.5cm;
              mso-footer: f1;
              mso-footer-margin: 0.8cm;
            }
  
            div.WordSection1 {
              page: WordSection1;
            }
  
            body {
              font-family: "TH Sarabun New", "Sarabun", Arial, sans-serif;
              font-size: 16px;
              line-height: 1.6;
              color: #111;
            }
  
            h1, h2, h3 {
              line-height: 1.3;
            }
  
            p {
              margin: 0 0 10px;
            }
  
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 12px 0;
            }
  
            table, th, td {
              border: 1px solid #333;
            }
  
            th, td {
              padding: 8px;
              vertical-align: top;
            }
  
            img {
              max-width: 100%;
              height: auto;
            }
  
            ul, ol {
              margin-top: 0;
              margin-bottom: 12px;
            }
  
            .document-title {
              text-align: center;
              font-size: 22px;
              font-weight: bold;
              margin-bottom: 20px;
            }
  
            p.MsoFooter {
              margin: 0;
              font-family: "TH Sarabun New", "Sarabun", Arial, sans-serif;
              font-size: 12px;
              color: #8c8c8c;
              text-align: right;
              border-top: 1px solid #d9d9d9;
              padding-top: 8px;
            }
  
            #hrdftrtbl {
              margin: 0;
              padding: 0;
              border: 0;
              width: 0;
              height: 0;
              overflow: hidden;
            }
  
            #hrdftrtbl td {
              border: 0;
              padding: 0;
            }
          </style>
        </head>
  
        <body>
          <div class="WordSection1">
            ${title ? `<div class="document-title">${title}</div>` : ""}
            ${html}
          </div>
  
          <!-- Word footer definition -->
          <table id="hrdftrtbl" cellspacing="0" cellpadding="0">
            <tr>
              <td>
                <div style="mso-element:footer" id="f1">
                  <p class="MsoFooter">
                    ${footerText}
                  </p>
                </div>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `;
  };

  const createExportElement = (html: string) => {
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.width = "100vw";
    overlay.style.height = "100vh";
    overlay.style.background = "#ffffff";
    overlay.style.zIndex = "1000000";
    overlay.style.pointerEvents = "auto";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.fontFamily = `"TH Sarabun New", "Sarabun", Arial, sans-serif`;
    overlay.style.fontSize = "18px";
    overlay.style.color = "#555";
    overlay.innerText = "กำลังสร้างไฟล์ PDF...";
  
    const container = document.createElement("div");
  
    container.style.position = "fixed";
    container.style.left = "0";
    container.style.top = "0";
    container.style.width = "794px";
    container.style.minHeight = "1123px";
    container.style.background = "#ffffff";
  
    /**
     * ให้ export element อยู่ข้างล่าง overlay
     * แต่ยัง visible จริง เพื่อให้ html2canvas จับได้
     */
    container.style.zIndex = "999999";
    container.style.pointerEvents = "none";
    container.style.overflow = "visible";
    container.style.opacity = "1";
    container.style.visibility = "visible";
    container.style.display = "block";
  
    container.innerHTML = html;
  
    document.body.appendChild(container);
    document.body.appendChild(overlay);
  
    return {
      container,
      overlay,
    };
  };

  export const exportHtmlToPdf = async (
    html: string,
    options?: ExportOptions
  ) => {
    if (!html?.trim()) {
      throw new Error("ไม่มีข้อมูล HTML สำหรับ export PDF");
    }
  
    const fileName = options?.fileName || "document.pdf";
    const title = options?.title || "";
    const footerText = getExportFooterText();
    const wrappedHtml = wrapHtmlForExport(html, title);
  
    const { container, overlay } = createExportElement(wrappedHtml);
  
    try {
      const html2pdf = (await import("html2pdf.js")).default;
  
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 500));
  
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
  
      const images = Array.from(container.querySelectorAll("img"));
  
      await Promise.all(
        images.map((img) => {
          if (img.complete) return Promise.resolve();
  
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        })
      );
  
      const targetElement = container.querySelector(
        ".export-document"
      ) as HTMLElement;
  
      if (!targetElement) {
        throw new Error("ไม่พบ element สำหรับ export PDF");
      }
  
      const footerImage = await createPdfFooterImage(footerText);
  
      const pdfOptions: any = {
        // เพิ่ม bottom margin ให้เนื้อหาไม่ชน footer
        margin: [10, 10, 22, 10],
        filename: fileName,
        image: {
          type: "jpeg",
          quality: 0.98,
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: 0,
          windowWidth: 794,
          windowHeight: Math.max(targetElement.scrollHeight, 1123),
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
        pagebreak: {
          mode: ["css", "legacy"],
        },
      };
  
      const worker = html2pdf()
        .set(pdfOptions)
        .from(targetElement)
        .toPdf();
  
      const pdf = await worker.get("pdf");
  
      addFooterToEveryPdfPage(pdf, footerImage);
  
      await worker.save();
    } finally {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
  
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
    }
  };
export const exportHtmlToDocx = async (
  html: string,
  options?: ExportOptions
) => {
  if (!html?.trim()) {
    throw new Error("ไม่มีข้อมูล HTML สำหรับ export DOCX");
  }

  const fileName = options?.fileName || "document.docx";
  const title = options?.title || "";
  const fullHtml = wrapHtmlForDocx(html, title);

  const docxBlob = await asBlob(fullHtml);

  const blob =
    docxBlob instanceof Blob
      ? docxBlob
      : new Blob([docxBlob], {
          type: wordMimeType,
        });

  saveAs(blob, fileName);
};